// ============================================================================
// sql_db_view 配置解析脚本（v20 必做项：独立生成，不依赖 remotePages/index.ts）
// ----------------------------------------------------------------------------
// 职责：融合「接口数据」+「前端 TS 配置」，产出页面壳 / rightTable 消费的 pageConfig。
//
// 与 remotePages getPageConfig 的两点区别（§4.4 ④ 白名单裁剪）：
//   ① 默认配置只保留搜索+展示相关白名单项 —— 无增删改（addData/updateData/...）、
//      无普通搜索（searchConfig）、无权限位（titleList.is_create/...）；
//   ② 只支持高级搜索（带运算符），is_show_advantage 恒 true / is_show_normal_search 恒 false。
//
// URL 策略（v10/v23）：**全部显式声明**，不做 `_list` → `_pivot_table_rust` 字符串替换派生：
//   queryData                  /db_view/sqlview_query_{flag}_list
//   countData                  /db_view/sqlview_query_{flag}_count
//   queryConditionGroupData    /db_view/sqlview_query_{flag}_group
//   getData                    /db_view/sqlview_get_{flag}
//   field_search_config        /db_view/sqlview_obtain_{flag}_field_search_config
//   pivot_table_url            /db_view/sqlview_query_{flag}_pivot_table_rust
//   pivot_charts_url           /db_view/sqlview_query_{flag}_pivot_charts_rust
//
// ★ v24（决策 24）：本组件不做任何按钮权限校验 —— 不实现 getApiPermission、
//   不派生 is_create/is_update 权限位、按钮可见性完全由白名单配置决定。
// ============================================================================
import { cloneDeep } from 'lodash';
import { getApiData } from 'liyu-pc-base/api/api-model';
import { useEnv, useGlobMap, useCellComponentMap, useCellFormatExcelValueMap } from 'liyu-pc-base/init-plugin';
import { modify_by_i18n_config } from 'liyu-pc-base/components/remotePages/i18n_config';
import type { SqlViewPageConfig } from './typing';

/** app_flag 固化为常量（v22）：本组件只服务 /db_view/*，不解析、不传参 */
export const DB_VIEW_APP_FLAG = 'db_view';

/** §4.4 ④ 白名单：默认配置/接口数据只保留这些键（搜索+展示） */
const CONFIG_WHITELIST = [
  'btn_list',
  'head_btn_list',
  'is_selection',
  'rowSelection',
  'is_pagination',
  'pageSize',
  'page_size_options',
  'customCell',
  'customRow',
  'customHeaderCell',
  'isView',
  'default_hide_view',
  'enable_view_types',
  'default_view_type',
  'pivot_table_max_records',
  'advanced_base_conditions',
  'default_search_condition_config_list',
  'condition_group_config_list',
  'base_search_condition_config_list',
  'baseColumns',
  'rowKey', // ★ v29.24：行唯一键（单主键 → 列名；0 个或 ≥2 个主键 → '_id'，v29.40），透传给 tablePro
  'default_column_field',
  'required_condition',
  'refresh_type',
  'i18n',
  'detailConfigs',
] as const;

/** 从任意配置对象中摘出白名单键（白名单外的键一律丢弃） */
const pickWhitelist = (source: any): Record<string, any> => {
  const out: Record<string, any> = {};
  if (!source) return out;
  for (const key of CONFIG_WHITELIST) {
    if (source[key] !== undefined) {
      out[key] = source[key];
    }
  }
  return out;
};

// ---------------- TS 配置加载（configs/{group}/{flag}.ts，仅增量覆盖、不重复写列） ----------------

const ts_config_map: { [key: string]: any } = {};
const init_ts_config = () => {
  if (Object.keys(ts_config_map).length > 0) {
    return;
  }
  const allTsConfigs: any = import.meta.glob('liyu-pc-base/components/sql_db_view/configs/*/*.ts', {
    eager: true,
  });
  const customTsConfigs: any = useGlobMap('sql_db_view');
  Object.assign(allTsConfigs, customTsConfigs);
  Object.keys(allTsConfigs).forEach(full_file_name => {
    const file_name_list = full_file_name.split('/');
    const file_name = file_name_list[file_name_list.length - 1].replace('.ts', '');
    if (file_name.endsWith('-config')) {
      return;
    }
    ts_config_map[file_name] = allTsConfigs[full_file_name].default;
  });
};

// ---------------- 列/按钮合并（与 remotePages/index.ts 同机制） ----------------

const mergeBtnList = (config: any[], customConfig: any[]) => {
  if (!customConfig) return config;
  const result = cloneDeep(config || []);
  const btnMap = new Map();
  result.forEach((btn, index) => {
    delete btn._key;
    btnMap.set(btn.name, index);
  });
  let last_order = 0;
  customConfig.forEach((customBtn: any) => {
    if (!customBtn.order) {
      customBtn.order = last_order + 1;
    }
    last_order = customBtn.order;
    customBtn._key = `custom_${customBtn.name}_${Math.random()}`;
    const existingIndex = btnMap.get(customBtn.name);
    if (existingIndex !== undefined) {
      const existingBtn = result[existingIndex];
      if (customBtn.children && existingBtn.children) {
        existingBtn.children = mergeBtnList(existingBtn.children, customBtn.children);
      }
      const temp = cloneDeep(customBtn);
      delete temp.children;
      Object.assign(existingBtn, temp);
    } else {
      const temp = cloneDeep(customBtn);
      result.push(temp);
      if (temp.children) {
        temp.children = mergeBtnList([], temp.children);
      }
    }
  });
  return result;
};

const mergeDataFunc: Record<string, Function> = {
  btn_list: mergeBtnList,
  head_btn_list: mergeBtnList,
};

const mergeData = (config: any, customConfig: any) => {
  const res = cloneDeep(config);
  Object.keys(customConfig).forEach(key => {
    if (mergeDataFunc[key]) {
      res[key] = mergeDataFunc[key](config[key], customConfig[key]);
    } else {
      res[key] = customConfig[key];
    }
  });
  Object.assign(config, res);
};

// ---------------- 列后处理（cellComponent → 导出格式映射，与 remotePages 同机制） ----------------

const processBaseColumns = (config: any) => {
  if (!config || !Array.isArray(config)) {
    return config;
  }
  const cellComponentMap = useCellComponentMap();
  const cellFormatExcelValueMap = useCellFormatExcelValueMap();
  for (const item of config) {
    if (item.cellComponent && !item.formatExcelValue) {
      const cellComponentData = cellComponentMap[item.cellComponent];
      if (cellComponentData) {
        const defaultExcelFormat = cellComponentData.defaultExcelFormat;
        const cellFormatExcelValue = cellFormatExcelValueMap[defaultExcelFormat];
        if (cellFormatExcelValue) {
          item.formatExcelValue = cellFormatExcelValue.flag;
        }
      }
    }
  }
  return config;
};

const processDataFunc: Record<string, Function> = {
  baseColumns: processBaseColumns,
};

const processData = (config: any) => {
  Object.keys(config).forEach(key => {
    if (processDataFunc[key]) {
      config[key] = processDataFunc[key](config[key]);
    }
  });
};

// ---------------- 运行时 URL（v10/v23：显式声明，不做字符串替换派生） ----------------

export interface SqlViewRuntimeUrls {
  queryData: string;
  countData: string;
  queryConditionGroupData: string;
  getData: string;
  fieldSearchConfigUrl: string;
  obtainColumnDataUrl: string;
  pivotTableUrl: string;
  pivotChartsUrl: string;
}

export const buildRuntimeUrls = (view_flag: string): SqlViewRuntimeUrls => ({
  queryData: `/${DB_VIEW_APP_FLAG}/sqlview_query_${view_flag}_list`,
  countData: `/${DB_VIEW_APP_FLAG}/sqlview_query_${view_flag}_count`,
  queryConditionGroupData: `/${DB_VIEW_APP_FLAG}/sqlview_query_${view_flag}_group`,
  getData: `/${DB_VIEW_APP_FLAG}/sqlview_get_${view_flag}`,
  fieldSearchConfigUrl: `/${DB_VIEW_APP_FLAG}/sqlview_obtain_${view_flag}_field_search_config`,
  obtainColumnDataUrl: `/${DB_VIEW_APP_FLAG}/sqlview_obtain_${view_flag}_column_data`,
  pivotTableUrl: `/${DB_VIEW_APP_FLAG}/sqlview_query_${view_flag}_pivot_table_rust`,
  pivotChartsUrl: `/${DB_VIEW_APP_FLAG}/sqlview_query_${view_flag}_pivot_charts_rust`,
});

// ---------------- 取数 + 配置融合主入口（v26：合一，async） ----------------

export const getSqlDbViewPageConfig = async (
  modalType: string, // 形如 `db_view-{flag}`
  org_id: string | number,
  t: any,
  options?: {
    to_i18n?: boolean;
  },
): Promise<SqlViewPageConfig> => {
  const { to_i18n = true } = options || {};
  init_ts_config();
  const view_flag = modalType.split('-')[1];
  const env = useEnv();

  // ★ 取数（v26 并入；v25 起两路并行）：baseColumns / base_search_condition_config_list
  //   完全以 DbViewColumn（get_page_config 编译返回）为单一真相源。
  //   列级覆盖接口 override_column_config 已于 v29.22 随后端一并删除（新通用组件不做列级覆盖）。
  const [defaultRes, serverRes] = await Promise.all([
    getApiData({ org_id, flag: view_flag }, `/${DB_VIEW_APP_FLAG}/get_page_config`, false, {
      skip_error: true,
    }),
    getApiData(
      { org_id, flag: `${DB_VIEW_APP_FLAG}-${view_flag}_config` },
      '/sys/get_global_custom_data_by_flag_without_org_id',
    ),
  ]);

  // ① 白名单默认值（v20/v27：默认值只含 §4.4 ④ 白名单字段，无增删改 URL、
  //    无 searchConfig、无权限位、无 query_params/get_db_alias 等通用页字段）
  const defaultConfig: SqlViewPageConfig = {
    is_selection: false,
    rowSelection: { type: 'checkbox', checkStrictly: false },
    is_pagination: true,
    pageSize: 50,
    page_size_options: ['10', '20', '50', '100', '200', '500', '1000', '3000', '10000'],
    customCell: null,
    customRow: null,
    customHeaderCell: null,
    isView: true,
    default_hide_view: false,
    enable_view_types: ['normal', 'pivot', 'pivot_charts'],
    default_view_type: 'normal',
    pivot_table_max_records: 10000,
    advanced_base_conditions: [],
    default_search_condition_config_list: null,
    condition_group_config_list: null,
    base_search_condition_config_list: null,
    baseColumns: null,
    rowKey: 'id', // ★ v29.24：兜底与 tablePro 默认一致；接口下发时被覆盖（主键列名 / '_id'）
    default_column_field: [],
    required_condition: [],
    refresh_type: '手动',
    i18n: null,
    detailConfigs: {},
    btn_list: null,
    head_btn_list: null,
  };

  // ② 接口 defaultConfig（get_page_config：baseColumns + 高级搜索 + 白名单 meta）——只摘白名单
  Object.assign(defaultConfig, pickWhitelist(defaultRes || {}));

  // ③ serverConfig（全局自定义配置）——同样只摘白名单
  Object.assign(defaultConfig, pickWhitelist(serverRes || {}));

  const mergedConfig = cloneDeep(defaultConfig);

  // ④ 前端 TS 配置（configs/{group}/{flag}.ts）——增量覆盖；★ 列不在这里写（DB 单一真相源）
  const ts_config = ts_config_map[view_flag] || ts_config_map[modalType];
  if (ts_config) {
    const ts_config_data = pickWhitelist(cloneDeep(ts_config(t, { model_app: DB_VIEW_APP_FLAG })));
    mergeData(mergedConfig, ts_config_data);
  }

  // ⑤ 列后处理（cellComponent → formatExcelValue）
  // ★ v27：运行时 URL **不进 PageConfig**（类型只含白名单字段）——
  //   rightTable 用 buildRuntimeUrls(view_flag) 自行派生 8 条 sqlview_* URL。
  processData(mergedConfig);

  let result: any = mergedConfig;
  if (to_i18n && env.VITE_APP_ENABLE_ZH_EN) {
    // modify_by_i18n_config 就地处理 mergedConfig（i18n 在白名单内），返回值即同一对象
    result = modify_by_i18n_config(mergedConfig, t, modalType);
  }
  return result as SqlViewPageConfig;
};
